/**
 * P0-16/17/18: GameBridge —— 新旧桥接层。
 *
 * 职责：
 * - getGame(): 返回全局 gamePage 单例（只读约定；游戏未就绪时返回 null，
 *   组件应据此挂 loading 态）。
 * - subscribeTick(listener): 包装 dojo.subscribe("ui/update")，
 *   返回取消订阅函数——修复旧代码订阅不取消的泄漏
 *   （js/jsx/left.jsx.js:944-949 的订阅永不退订）。
 * - subscribeTopic(topic, listener): 通用 dojo topic 包装。
 *
 * 引擎事实（见 docs/ui-refactor-plan.md §4.2）：
 * - 每 tick 由 js/ui.js:650 → game.js:2445-2450 经 dojo.publish 广播 "ui/update"。
 * - 其余常用 topic："game/options"（game.js:2583）、
 *   "server/undoStateChanged"（game.js:2442）、"game/beforesave"（game.js:2525）。
 * - 本库 dojo 为 1.6（game.js:2446 的版本判断），退订用 dojo.unsubscribe(handle)。
 */

/** 引擎单例的最小类型占位。各岛迁移时按实际消费的 surface 逐步收窄。 */
export type GamePage = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type TickListener = (game: GamePage) => void;
export type TopicListener = (...args: unknown[]) => void;

interface DojoLike {
  subscribe(topic: string, listener: TopicListener): unknown;
  unsubscribe(handle: unknown): void;
}

declare global {
  interface Window {
    gamePage?: GamePage;
    dojo?: DojoLike;
  }
}

const noop = () => {};

/**
 * P3-05: DesktopUI.render() 每帧 dojo.empty 重建的容器 id
 *（js/ui.js:384；containerId 由 index.html initGame 传入，固定为 "gameContainerId"）。
 */
export const DOJO_EMPTY_CONTAINER_ID = "gameContainerId";

/**
 * P3-05: 防御性断言——React root 不得挂在会被 dojo.empty 清掉的节点内
 *（含挂载点自身）。DesktopUI.render() 会清空 dojo.byId(containerId) 的全部
 * 子节点（js/ui.js:384），挂在其中的 React root 会在下一次 tab 切换/render
 * 时被静默移除。中栏共存期 React 分区必须挂在该容器的**兄弟节点**
 *（见 webui/src/tabs/midPanel.ts）。mountIsland（main.tsx）每次挂载前调用。
 */
export function assertReactMountSafe(el: Element): void {
  let node: Element | null = el;
  while (node) {
    if (node.id === DOJO_EMPTY_CONTAINER_ID) {
      throw new Error(
        "[GameBridge] React root 挂载点位于 #" + DOJO_EMPTY_CONTAINER_ID + " 内，" +
          "会被 DesktopUI.render() 的 dojo.empty 清除（js/ui.js:384）；" +
          "请改挂到该容器的兄弟节点",
      );
    }
    node = node.parentElement;
  }
}

function getDojo(): DojoLike | null {
  const d = (globalThis as { dojo?: DojoLike }).dojo;
  if (!d || typeof d.subscribe !== "function" || typeof d.unsubscribe !== "function") {
    return null;
  }
  return d;
}

/** 返回全局 gamePage 单例；游戏尚未初始化完成时返回 null。 */
export function getGame(): GamePage | null {
  return (globalThis as { gamePage?: GamePage }).gamePage ?? null;
}

/** 通用 dojo topic 订阅；返回取消订阅函数。dojo 未就绪时返回 no-op。 */
export function subscribeTopic(topic: string, listener: TopicListener): () => void {
  const d = getDojo();
  if (!d) {
    console.warn(`[GameBridge] dojo 未就绪，跳过订阅: ${topic}`);
    return noop;
  }
  const handle = d.subscribe(topic, listener);
  let active = true;
  return () => {
    if (!active) {
      return;
    }
    active = false;
    d.unsubscribe(handle);
  };
}

/**
 * 每 tick 订阅（"ui/update"）。返回取消订阅函数。
 * listener 收到的是 gamePage 单例（dojo.publish 的载荷）。
 */
export function subscribeTick(listener: TickListener): () => void {
  return subscribeTopic("ui/update", listener as TopicListener);
}

// ---------------------------------------------------------------------------
// P5-01: 新版本提示桥接
// ---------------------------------------------------------------------------

export type NewVersionListener = (available: boolean) => void;

let newVersionListener: NewVersionListener | null = null;

export function setNewVersionListener(l: NewVersionListener | null): void {
  newVersionListener = l;
}

export function showNewVersion(): void {
  newVersionListener?.(true);
}
